import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AddClientDialog } from "@/components/clients/AddClientDialog";
import { ReconnectClientDialog } from "@/components/clients/ReconnectClientDialog";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Check, Linkedin, Loader2, ArrowRight, ArrowLeft, Users, Trash2, Plus, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { permanentlyDeleteCampaign } from "@/lib/campaignHelpers"; // Keep for potential future use
import { cn } from "@/lib/utils";

interface SimplifiedConnectStepProps {
    onNext: (clientId: string) => void;
    onBack?: () => void; // For going back to mode selection
    initialClientId?: string;
    guestToken?: string;
    demoMode?: boolean;
    isOnboarding?: boolean; // Hide delete button in onboarding flow
}

export const SimplifiedConnectStep = ({ onNext, onBack, initialClientId, guestToken, demoMode, isOnboarding = false }: SimplifiedConnectStepProps) => {
    const queryClient = useQueryClient();
    const { workspace, refetch: refetchWorkspace } = useWorkspace();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [clientToDelete, setClientToDelete] = useState<any>(null);
    const [allClients, setAllClients] = useState<any[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [pendingConnection, setPendingConnection] = useState<{
        clientId: string;
        isVerifying: boolean;
    } | null>(null);
    const [reconnectDialogOpen, setReconnectDialogOpen] = useState(false);
    const [clientToReconnect, setClientToReconnect] = useState<any>(null);

    const deleteClientMutation = useMutation({
        mutationFn: async (client: any) => {
            // Use the unified delete-client-account edge function
            // @ts-ignore - accessing internal properties
            const supabaseUrl = supabase.supabaseUrl;
            // @ts-ignore - accessing internal properties
            const supabaseKey = supabase.supabaseKey;

            let response;
            
            if (guestToken) {
                // Guest flow - use anon key with guest token
                response = await fetch(`${supabaseUrl}/functions/v1/delete-client-account`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${supabaseKey}`,
                    },
                    body: JSON.stringify({ clientId: client.id, guestToken }),
                });
            } else {
                // Authenticated flow
                const { data: sessionData } = await supabase.auth.getSession();
                response = await fetch(`${supabaseUrl}/functions/v1/delete-client-account`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionData.session?.access_token}`,
                    },
                    body: JSON.stringify({ clientId: client.id }),
                });
            }

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || data.error || 'Failed to delete account');
            }

            return data;
        },
        onSuccess: (data) => {
            toast.success("Account disconnected successfully");
            // Remove from local state
            setAllClients(prev => prev.filter(c => c.id !== clientToDelete?.id));
            // Clear selection if deleted client was selected
            if (selectedClientId === clientToDelete?.id) {
                setSelectedClientId(null);
            }
            setClientToDelete(null);
            setDeleteDialogOpen(false);
            queryClient.invalidateQueries({ queryKey: ["clients"] });
            refetchWorkspace();
        },
        onError: (error) => {
            toast.error("Failed to disconnect account: " + error.message);
        },
    });

    const fetchClients = async (skipLoadingState = false): Promise<any[]> => {
        // Demo mode should never hit the database
        if (demoMode) {
            if (initialClientId) {
                const demoClients = [{
                    id: initialClientId,
                    linkedin_name: "Demo User",
                    email: "demo@wdcweb.com",
                    status: "connected",
                    headline: "CEO at Demo Corp",
                    profile_picture_url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
                    connections_count: Math.floor(Math.random() * 400) + 350
                }];
                setAllClients(demoClients);
                setSelectedClientId(initialClientId);
                if (!skipLoadingState) setLoading(false);
                return demoClients;
            }

            setAllClients([]);
            setSelectedClientId(null);
            if (!skipLoadingState) setLoading(false);
            return [];
        }

        if (!workspace?.id && !guestToken) return [];
        if (!skipLoadingState) setLoading(true);

        try {
            let clients: any[] = [];

        if (guestToken) {
            // Fetch using guest token RPC v2 (safer, returns limited fields, proper filtering)
            const { data: guestClients, error } = await supabase
                .rpc('get_guest_connected_accounts_v2', { token_uuid: guestToken });

            if (error) {
                console.error('Error fetching guest clients:', error);
            }

            if (guestClients && guestClients.length > 0) {
                clients = guestClients;
            }
        } else {
            // Fetch ALL clients including disconnected (so they can reconnect)
            const { data: workspaceClients, error } = await supabase
                .from("clients")
                    .select("*")
                    .eq("workspace_id", workspace?.id)
                    .neq("status", "archived")
                    .neq("status", "member_left")
                    .order("created_at", { ascending: false });

                if (error) {
                    console.error('Error fetching clients:', error);
                } else {
                    clients = workspaceClients || [];
                }
            }

            setAllClients(clients);
            
            // Auto-select: prefer initialClientId (if active), then first active client
            // Skip disconnected accounts for auto-selection
            const activeClients = clients.filter(c => c.status !== 'disconnected');
            
            if (initialClientId && activeClients.some(c => c.id === initialClientId)) {
                setSelectedClientId(initialClientId);
            } else if (activeClients.length > 0 && !selectedClientId) {
                setSelectedClientId(activeClients[0].id);
            } else if (activeClients.length === 0 && clients.length > 0) {
                // All accounts are disconnected - don't auto-select any
                setSelectedClientId(null);
            }
            
            return clients;
        } catch (error: any) {
            console.error('Error fetching clients:', error);
            if (error.message) {
                toast.error("Failed to load accounts: " + error.message);
            }
            return [];
        } finally {
            if (!skipLoadingState) setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, [workspace?.id, guestToken, initialClientId, demoMode]);

    const handleClientConnected = async (newClientId?: string) => {
        if (demoMode && newClientId) {
            setAllClients(prev => [...prev, {
                id: newClientId,
                linkedin_name: "Demo User",
                email: "demo@wdcweb.com",
                status: "connected",
                headline: "CEO at Demo Corp",
                profile_picture_url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
                connections_count: Math.floor(Math.random() * 400) + 350
            }]);
            setSelectedClientId(newClientId);
            setIsDialogOpen(false);
            return;
        }

        // Close dialog immediately
        setIsDialogOpen(false);

        // Set pending connection state for optimistic UI
        if (newClientId) {
            setPendingConnection({ clientId: newClientId, isVerifying: true });
        }

        // Invalidate queries once
        queryClient.invalidateQueries({ queryKey: ["clients"] });
        queryClient.invalidateQueries({ queryKey: ["workspace"] });

        // Store initial count for comparison
        const initialCount = allClients.length;

        // Polling-based refresh: retry until we see the new client or timeout
        // Use skipLoadingState=true to prevent visual refreshes
        const maxAttempts = 6;
        const pollInterval = 2000; // 2 seconds
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
            
            // Fetch clients silently (no loading state toggle)
            const freshClients = await fetchClients(true);
            
            // Check against freshly returned data (not stale state)
            if (newClientId && freshClients.some(c => c.id === newClientId)) {
                setPendingConnection(null);
                setSelectedClientId(newClientId);
                await refetchWorkspace();
                toast.success("Account connected successfully!");
                return;
            }
            
            // If no specific client ID, check if list grew
            if (!newClientId && freshClients.length > initialCount) {
                setPendingConnection(null);
                await refetchWorkspace();
                toast.success("Account connected successfully!");
                return;
            }
        }

        // Final update - do one visible refresh at the end
        setPendingConnection(null);
        await fetchClients(false);
        await refetchWorkspace();
        
        if (newClientId) {
            setSelectedClientId(newClientId);
        }
        toast.success("Account connected!");
    };

    const handleContinue = () => {
        if (selectedClientId) {
            onNext(selectedClientId);
        } else {
            toast.error("Please select a LinkedIn account first");
        }
    };

    const handleDeleteClick = (client: any) => {
        setClientToDelete(client);
        setDeleteDialogOpen(true);
    };

    // Check if more seats are available - use nullish coalescing for safety
    // max_seats=0 means "no seats configured" (treat as 0), not "unlimited"
    // In demo mode, only allow 1 seat total
    const maxSeats = demoMode ? 1 : (workspace?.max_seats ?? 0);
    const usedSeats = demoMode ? allClients.length : (workspace?.used_seats ?? 0);
    const canAddMoreAccounts = demoMode ? allClients.length < 1 : (workspace && usedSeats < maxSeats);
    
    // Hide add card while pending connection is verifying
    const showAddCard = canAddMoreAccounts && !pendingConnection;
    
    // Calculate total items for centering logic (include pending card)
    const totalItems = allClients.length + (showAddCard ? 1 : 0) + (pendingConnection ? 1 : 0);
    const shouldCenter = totalItems <= 2;

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Multi-account grid */}
            {allClients.length > 0 ? (
                <div className="space-y-4">
                    <div className={cn(
                        "flex flex-wrap gap-4",
                        shouldCenter ? "justify-center" : "justify-start"
                    )}>
                        {allClients.map((client) => {
                            const isDisconnected = client.status === 'disconnected';
                            
                            return (
                                <Card
                                    key={client.id}
                                    className={cn(
                                        "relative border transition-all w-full max-w-[280px]",
                                        isDisconnected 
                                            ? "border-destructive/40 bg-destructive/5 cursor-not-allowed"
                                            : selectedClientId === client.id
                                                ? "border-primary ring-2 ring-primary/20 shadow-md cursor-pointer hover:shadow-md"
                                                : "border-border/40 shadow-sm cursor-pointer hover:shadow-md"
                                    )}
                                    onClick={() => !isDisconnected && setSelectedClientId(client.id)}
                                >
                                    {/* Hide delete button in onboarding flow */}
                                    {!isOnboarding && !isDisconnected && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="absolute top-2 right-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8 rounded-full z-10"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick(client);
                                            }}
                                            title="Disconnect Account"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}

                                    <CardContent className="pt-6 pb-6 flex flex-col items-center text-center gap-4">
                                        <div className="relative">
                                            <div className={cn(
                                                "h-16 w-16 rounded-full overflow-hidden border bg-muted",
                                                isDisconnected && "opacity-60"
                                            )}>
                                                {client.profile_picture_url ? (
                                                    <img src={client.profile_picture_url} alt="Profile" className="h-full w-full object-cover" />
                                                ) : (
                                                    <div className="h-full w-full flex items-center justify-center text-muted-foreground font-bold text-lg">
                                                        {client.linkedin_name?.[0] || client.email?.[0] || "?"}
                                                    </div>
                                                )}
                                            </div>
                                            {isDisconnected ? (
                                                <div className="absolute bottom-0 right-0 bg-destructive text-destructive-foreground rounded-full p-1 ring-2 ring-background">
                                                    <AlertTriangle className="h-3 w-3" />
                                                </div>
                                            ) : selectedClientId === client.id && (
                                                <div className="absolute bottom-0 right-0 bg-primary text-primary-foreground rounded-full p-1 ring-2 ring-background">
                                                    <Check className="h-3 w-3" />
                                                </div>
                                            )}
                                        </div>

                                        <div className="space-y-1">
                                            <div className="flex items-center justify-center gap-2 flex-wrap">
                                                <h3 className={cn(
                                                    "font-semibold text-base",
                                                    isDisconnected && "text-muted-foreground"
                                                )}>{client.linkedin_name || "LinkedIn User"}</h3>
                                                {isDisconnected && (
                                                    <Badge variant="destructive" className="text-[10px] h-5 px-1.5">
                                                        Disconnected
                                                    </Badge>
                                                )}
                                                {!isDisconnected && client.is_premium && (
                                                    <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200 shadow-none">
                                                        Premium
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-muted-foreground text-sm max-w-[200px] mx-auto line-clamp-2">
                                                {client.headline || client.email}
                                            </p>
                                        </div>

                                        {isDisconnected ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setClientToReconnect(client);
                                                    setReconnectDialogOpen(true);
                                                }}
                                            >
                                                <RefreshCw className="h-4 w-4" />
                                                Reconnect
                                            </Button>
                                        ) : (
                                            <div className="flex items-center gap-1.5 text-muted-foreground bg-muted/30 px-3 py-1 rounded-full border border-border/50">
                                                <Users className="h-3.5 w-3.5" />
                                                <span className="text-xs font-medium">
                                                    {client.connections_count !== null && client.connections_count !== undefined
                                                        ? `${client.connections_count.toLocaleString()} connections`
                                                        : client.profile_fetch_pending
                                                            ? "Syncing..."
                                                            : "-- connections"
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}

                        {/* Pending connection verifying card - optimistic UI */}
                        {pendingConnection?.isVerifying && (
                            <Card
                                className="relative border border-primary/40 shadow-md w-full max-w-[280px]"
                            >
                                <CardContent className="pt-6 pb-6 flex flex-col items-center text-center gap-4">
                                    <div className="relative">
                                        <div className="h-16 w-16 rounded-full bg-muted border flex items-center justify-center">
                                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="font-semibold text-base">Verifying Account...</h3>
                                        <p className="text-muted-foreground text-sm">
                                            Setting up your connection
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-muted-foreground bg-muted/30 px-3 py-1 rounded-full border border-border/50">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        <span className="text-xs font-medium">Syncing...</span>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Add another account card */}
                        {showAddCard && (
                            <Card
                                className="border border-dashed border-border/60 shadow-sm transition-all cursor-pointer hover:shadow-md hover:border-primary/40 w-full max-w-[280px]"
                                onClick={() => setIsDialogOpen(true)}
                            >
                                <CardContent className="pt-6 pb-6 flex flex-col items-center justify-center text-center gap-3 h-full min-h-[180px]">
                                    <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                                        <Plus className="h-6 w-6 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-sm">Add Another Account</h3>
                                        <p className="text-muted-foreground text-xs mt-1">
                                            {maxSeats - usedSeats} seat{(maxSeats - usedSeats) !== 1 ? 's' : ''} available
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Navigation buttons */}
                    <div className="flex flex-col items-center gap-2 pt-4">
                        {/* Warning if all accounts are disconnected */}
                        {allClients.length > 0 && allClients.every(c => c.status === 'disconnected') && (
                            <p className="text-sm text-destructive flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4" />
                                Please reconnect an account to continue
                            </p>
                        )}
                        <div className="flex items-center gap-4">
                            {onBack && (
                                <Button
                                    variant="outline"
                                    onClick={onBack}
                                    size="lg"
                                    className="gap-2"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                    Change Mode
                                </Button>
                            )}
                            <Button
                                onClick={handleContinue}
                                size="lg"
                                className="gap-2"
                                disabled={!selectedClientId || allClients.find(c => c.id === selectedClientId)?.status === 'disconnected'}
                            >
                                Continue
                                <ArrowRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                // No accounts - show connect card
                <Card className="w-full max-w-sm mx-auto border border-border/40 shadow-sm transition-all hover:shadow-md">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="text-xl">Connect LinkedIn</CardTitle>
                        <CardDescription>
                            Connect your LinkedIn account to start automating your outreach.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center gap-3 pt-6 pb-8">
                        <Button onClick={() => setIsDialogOpen(true)} size="lg" className="w-full max-w-[200px] gap-2">
                            <Linkedin className="h-5 w-5" />
                            Connect
                        </Button>
                        {onBack && (
                            <Button
                                variant="outline"
                                onClick={onBack}
                                size="lg"
                                className="w-full max-w-[200px] gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Change Mode
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            <AddClientDialog
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                onSuccess={handleClientConnected}
                workspaceId={workspace?.id}
                guestToken={guestToken}
                demoMode={demoMode}
            />

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete LinkedIn Account</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure? This will disconnect the account, stop all campaigns, and remove all associated data. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => clientToDelete && deleteClientMutation.mutate(clientToDelete)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteClientMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Deleting...
                                </>
                            ) : (
                                "Delete Account"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reconnect Dialog for disconnected accounts */}
            {clientToReconnect && (
                <ReconnectClientDialog
                    clientId={clientToReconnect.id}
                    clientEmail={clientToReconnect.email}
                    open={reconnectDialogOpen}
                    onOpenChange={setReconnectDialogOpen}
                    onSuccess={() => {
                        // Refresh client list to get updated status
                        fetchClients();
                        setReconnectDialogOpen(false);
                        setClientToReconnect(null);
                        // Auto-select the reconnected account
                        setSelectedClientId(clientToReconnect.id);
                        toast.success("Account reconnected successfully!");
                    }}
                />
            )}
        </div>
    );
};
